import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  Logout,
  SearchOff,
  Radio,
  Add,
  Settings,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import NowPlaying from '../components/NowPlaying';
import HotKeyCard from '../components/HotKeyCard';
import PreviewSendModal from '../components/PreviewSendModal';
import HotKeyFormModal from '../components/HotKeyFormModal';
import StationSwitcher from '../components/StationSwitcher';
import NewsflowStationSettingsPanel from '../components/NewsflowStationSettingsPanel';
import { HotKey, Category as CategoryType, Station } from '../types';
import apiService, { isSessionExpiredError } from '../services/api';
import { figma } from '../theme/figmaTokens';
import { applyCategoryColorsToHotKeys } from '../utils/applyCategoryColorsToHotKeys';

const topBarIcon = '#999999';

const Dashboard: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [station, setStation] = useState<Station | null>(null);
  const [hotKeys, setHotKeys] = useState<HotKey[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** True when hot quu list failed to load (vs successful empty response) */
  const [hotKeysLoadError, setHotKeysLoadError] = useState(false);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  

  
  // Preview modal
  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    hotKey: HotKey | null;
    mode: 'start' | 'stop';
  }>({
    open: false,
    hotKey: null,
    mode: 'start',
  });

  // Create/Edit form modal
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [hotKeyToEdit, setHotKeyToEdit] = useState<HotKey | null>(null);
  const [newsflowSettingsOpen, setNewsflowSettingsOpen] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      let currentStation: Station | null = null;
      try {
        setLoading(true);
        setError('');
        
        // Get selected station from localStorage
        const selectedStationData = localStorage.getItem('selectedStation');
        const userData = localStorage.getItem('userData');
        console.log('Dashboard: selectedStationData:', selectedStationData);
        console.log('Dashboard: userData:', userData);
        
        if (selectedStationData) {
          try {
            const stationData = JSON.parse(selectedStationData);
            currentStation = {
              id: stationData.id.toString(),
              name: stationData.stationName,
              callLetters: stationData.callLetters ?? '',
              nowPlaying: undefined,
            };
            console.log('Dashboard: Using selected station:', currentStation);
          } catch (error) {
            console.error('Error parsing selected station:', error);
          }
        }
        
        if (!currentStation) {
          // Fallback to default station
          currentStation = {
            id: '180638',
            name: 'Default Station',
            callLetters: '',
            nowPlaying: undefined,
          };
        }
        
        setStation(currentStation);
        console.log('Dashboard: Set station:', currentStation);
        
        // Load categories from API (station-scoped)
        console.log('Loading categories from API for station:', currentStation.id);
        const categoriesData = await apiService.getCategories(currentStation.id);
        console.log('Categories loaded:', categoriesData);
        setCategories(categoriesData);
        
        // Load hot keys for the current station (pass station id explicitly)
        console.log('Loading hot keys from API for station:', currentStation.id);
        const hotKeysData = await apiService.getHotKeysForStation(currentStation.id);
        console.log('Hot keys loaded:', hotKeysData);
        console.log('Number of hot keys:', hotKeysData.length);
        setHotKeys(hotKeysData);
        setHotKeysLoadError(false);
        
        // Clear any previous error if we successfully got data (even if empty)
        setError('');
        
      } catch (err: any) {
        console.error('Error loading data:', err);
        // 401 triggers session-expired redirect in apiService; avoid flashing a soft banner.
        if (isSessionExpiredError(err)) {
          return;
        }
        const detail =
          err?.message || err?.response?.data?.message || 'Failed to load data from API.';
        setError(detail);
        setHotKeysLoadError(true);
        // Try to load categories as fallback (same station when known)
        try {
          const categoriesData = await apiService.getCategories(currentStation?.id);
          setCategories(categoriesData);
        } catch (catError) {
          console.error('Error loading categories:', catError);
          setCategories([
            {
              id: 'Misc',
              name: 'Misc',
              color: '#1976d2',
              isPrivate: false,
              isDefault: true,
            }
          ]);
        }
        // If hot keys API fails, show no messages instead of mock data
        setHotKeys([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);



  // Handle hot key actions
  const handleHotKeyClick = (hotKey: HotKey) => {
    setPreviewModal({
      open: true,
      hotKey,
      mode: hotKey.isActive ? 'stop' : 'start',
    });
  };

  const handlePreviewSuccess = () => {
    if (!station) return;
    apiService
      .getHotKeysForStation(station.id.toString())
      .then(setHotKeys)
      .catch((err) => {
        console.error('Error refreshing hot keys after action:', err);
        if (isSessionExpiredError(err)) return;
        setError('Failed to refresh messages from API.');
      });
  };

  const handleOpenCreate = () => {
    setHotKeyToEdit(null);
    setFormModalOpen(true);
  };

  const handleOpenEdit = (hotKey: HotKey) => {
    setHotKeyToEdit(hotKey);
    setFormModalOpen(true);
  };

  const handleFormSuccess = () => {
    if (!station) return;
    apiService
      .getHotKeysForStation(station.id.toString())
      .then(setHotKeys)
      .catch((err) => {
        console.error('Error refreshing hot keys after save:', err);
        if (isSessionExpiredError(err)) return;
        setError('Failed to refresh messages.');
      });
  };

  const hotKeysForDisplay = useMemo(
    () => applyCategoryColorsToHotKeys(hotKeys, categories),
    [hotKeys, categories]
  );

  // Filter hot keys (colors merged from station category API list)
  const filteredHotKeys = hotKeysForDisplay.filter(hotKey => {
    const matchesCategory = selectedCategory === 'all' || hotKey.categoryId === selectedCategory;
    const matchesActive = activeFilter === 'all' || 
      (activeFilter === 'active' && hotKey.isActive) ||
      (activeFilter === 'inactive' && !hotKey.isActive);
    const matchesSearch = hotKey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hotKey.messageText.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesActive && matchesSearch;
  });

  // Sort categories: default first, then by name
  const sortedCategories = categories.sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return a.name.localeCompare(b.name);
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const resetFiltersToDefaults = () => {
    setSelectedCategory('all');
    setActiveFilter('all');
    setSearchTerm('');
  };

  const handleStationChange = (newStation: any) => {
    console.log('Dashboard: Station changed to:', newStation);
    // #38: Category and Status (and search) reset when switching stations
    resetFiltersToDefaults();

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Update station info
        const currentStation = {
          id: newStation.id.toString(),
          name: newStation.stationName || newStation.name,
          callLetters: newStation.callLetters ?? '',
          nowPlaying: undefined,
        };
        setStation(currentStation);
        console.log('Dashboard: Updated station:', currentStation);
        
        // Reload categories from API for the new station
        console.log('Dashboard: Loading categories from API for station:', currentStation.id);
        const categoriesData = await apiService.getCategories(currentStation.id);
        console.log('Dashboard: Categories loaded:', categoriesData);
        setCategories(categoriesData);
        
        // Reload hot keys for the new station (pass station ID explicitly)
        console.log('Dashboard: Loading hot keys for new station with id:', currentStation.id);
        const hotKeysData = await apiService.getHotKeysForStation(currentStation.id.toString());
        console.log('Dashboard: Hot keys loaded for new station:', hotKeysData);
        console.log('Dashboard: Number of hot keys for new station:', hotKeysData.length);
        setHotKeys(hotKeysData);
        setHotKeysLoadError(false);
        
        // Clear any previous error if we successfully got data (even if empty)
        setError('');
        
      } catch (err: any) {
        console.error('Dashboard: Error loading data for new station:', err);
        setError('Failed to load data for the new station. Using existing data.');
        setHotKeysLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: figma.pageBg }}>
        <CircularProgress sx={{ color: figma.cyan }} />
      </Box>
    );
  }

  console.log('Dashboard: Rendering with station:', station);

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: figma.pageBg }}>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#000000', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <Toolbar sx={{ gap: 1.5, py: 1, minHeight: 64 }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/Quu_Logo_Gray.png`}
            alt="Quu"
            sx={{ height: 36, width: 'auto', mr: 0.5, ml: { xs: 0, sm: 1 } }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ color: '#ffffff', fontWeight: 700, letterSpacing: '0.04em' }}
          >
            HotQuus
          </Typography>
          <Box
            onClick={handleOpenCreate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenCreate();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Create HotQuu"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 1,
              cursor: 'pointer',
              borderRadius: 1,
              outline: 'none',
              '&:focus-visible': { boxShadow: '0 0 0 2px #4DAADB' },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#4DAADB',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '&:hover': { backgroundColor: '#409dcf' },
              }}
            >
              <Add sx={{ fontSize: 22 }} />
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            onClick={() => setNewsflowSettingsOpen((o) => !o)}
            sx={{ color: topBarIcon }}
            aria-label="Station Newsflow settings"
            aria-pressed={newsflowSettingsOpen}
          >
            <Settings />
          </IconButton>
          <StationSwitcher onStationChange={handleStationChange} />
          <IconButton onClick={handleLogout} sx={{ color: topBarIcon }} aria-label="Log out">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {newsflowSettingsOpen && station && (
          <Box sx={{ mb: 3 }}>
            <NewsflowStationSettingsPanel stationId={station.id} categories={sortedCategories} />
          </Box>
        )}

        {/* Station Info and Now Playing */}
        {station && (
          <Box sx={{ mb: 3 }}>
            <NowPlaying 
              stationName={station.name}
              callLetters={station.callLetters ?? ''}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              activeFilter={activeFilter}
              onActiveFilterChange={setActiveFilter}
              categories={sortedCategories}
            />
          </Box>
        )}

        {/* Create message + Search */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              textTransform: 'none',
              color: '#ffffff',
              '& .MuiButton-startIcon': { color: '#ffffff' },
              '& .MuiButton-startIcon svg': { color: '#ffffff' },
            }}
          >
            Create HotQuu
          </Button>
          <Grid container spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 200 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search HotQuus"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>



        {/* Hot Quus grid (flat list, not grouped by category) */}
        <Box>
          {filteredHotKeys.length > 0 && (
            <Grid container spacing={2}>
              {filteredHotKeys.map((hotKey) => (
                <Grid item xs={12} sm={6} md={4} key={hotKey.id}>
                  <HotKeyCard
                    hotKey={hotKey}
                    onCardClick={handleHotKeyClick}
                    onEditClick={handleOpenEdit}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {filteredHotKeys.length === 0 && (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 8,
                px: 3,
                backgroundColor: figma.cardBg,
                borderRadius: 1,
                border: `1px solid ${figma.border}`,
                boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
                maxWidth: 600,
                mx: 'auto',
                mt: 4,
              }}
            >
              {hotKeys.length === 0 ? (
                <>
                  <Radio 
                    sx={{ 
                      fontSize: 64, 
                      color: figma.textMutedLight,
                      mb: 2,
                      opacity: 0.7
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ mb: 1, fontWeight: 600, color: figma.text }}
                  >
                    {hotKeysLoadError ? 'Unable to load' : 'No HotQuus'}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ mb: 3, maxWidth: 400, mx: 'auto', color: figma.textMuted }}
                  >
                    {hotKeysLoadError
                      ? "HotQuus can't be retrieved at this time."
                      : "There aren't any HotQuus for this station."}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label="Station: Ready" 
                      color="success" 
                      variant="outlined" 
                      size="small"
                    />
                    <Chip 
                      label="HotQuus: 0" 
                      color="default" 
                      variant="outlined" 
                      size="small"
                    />
                  </Box>
                </>
              ) : (
                <>
                  <SearchOff 
                    sx={{ 
                      fontSize: 64, 
                      color: figma.textMutedLight,
                      mb: 2,
                      opacity: 0.7
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ mb: 1, fontWeight: 600, color: figma.text }}
                  >
                    No HotQuus Found
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ mb: 3, maxWidth: 400, mx: 'auto', color: figma.textMuted }}
                  >
                    No HotQuus match your current search or filter settings.
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label="Clear search"
                      color="info"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSelectedCategory('all');
                        setSearchTerm('');
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Container>



      {/* Preview/Send Modal */}
      <PreviewSendModal
        open={previewModal.open}
        onClose={() => setPreviewModal({ open: false, hotKey: null, mode: 'start' })}
        hotKey={previewModal.hotKey}
        mode={previewModal.mode}
        onSuccess={handlePreviewSuccess}
        onEdit={handleOpenEdit}
      />

      {/* Create/Edit Hot Quu */}
      <HotKeyFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleFormSuccess}
        initialHotKey={hotKeyToEdit}
        categories={sortedCategories}
      />
    </Box>
  );
};

export default Dashboard; 